import React, { useState } from 'react';
import { X, Mic, MicOff, Video, VideoOff, ShieldCheck, UserX, VolumeX, Search } from 'lucide-react';
import { Participant } from '../types';

interface ParticipantListProps {
  participants: Participant[];
  currentUserId: string;
  isHost: boolean;
  onClose: () => void;
  onMuteParticipant: (id: string) => void;
  onRemoveParticipant: (id: string) => void;
  onMuteAll: () => void;
}

export const ParticipantList: React.FC<ParticipantListProps> = ({
  participants,
  currentUserId,
  isHost,
  onClose,
  onMuteParticipant,
  onRemoveParticipant,
  onMuteAll,
}) => {
  const [filter, setFilter] = useState('');

  const filtered = participants.filter((p) =>
    p.name.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div className="w-full h-full bg-[#121622] flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-slate-800/80 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <h3 className="font-semibold text-white text-sm">Participants</h3>
          <span className="px-2 py-0.5 rounded-full bg-slate-800 text-xs font-semibold text-slate-300">
            {participants.length}
          </span>
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Host Action: Mute All */}
      {isHost && (
        <div className="p-3 border-b border-slate-800/60 bg-[#0c0f17]/40 flex justify-end">
          <button
            onClick={onMuteAll}
            className="flex items-center space-x-1.5 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-xs font-semibold text-slate-200 transition border border-slate-700/60"
          >
            <VolumeX className="w-3.5 h-3.5 text-rose-400" />
            <span>Mute All</span>
          </button>
        </div>
      )}

      {/* Search Input */}
      <div className="p-3 border-b border-slate-800/40">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search participants..."
            className="w-full pl-9 pr-3 py-1.5 rounded-lg bg-slate-900 border border-slate-800 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition"
          />
        </div>
      </div>

      {/* Participant List */}
      <div className="flex-1 overflow-y-auto divide-y divide-slate-800/40 p-2">
        {filtered.map((p) => {
          const isCurrentUser = p.userId === currentUserId;
          return (
            <div
              key={p.id}
              className="py-2.5 px-2 rounded-xl flex items-center justify-between hover:bg-slate-800/30 transition group"
            >
              <div className="flex items-center space-x-2.5 min-w-0">
                <div className="w-8 h-8 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
                  {p.name[0]?.toUpperCase() || 'U'}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center space-x-1.5">
                    <span className="text-xs font-medium text-slate-200 truncate">
                      {p.name} {isCurrentUser && '(You)'}
                    </span>
                    {p.role === 'host' && (
                      <span className="flex-shrink-0 flex items-center space-x-0.5 text-blue-400 text-[10px] font-semibold">
                        <ShieldCheck className="w-3 h-3" />
                      </span>
                    )}
                  </div>
                  {p.handRaised && (
                    <span className="text-[10px] text-amber-400 font-semibold flex items-center space-x-0.5">
                      <span>✋ Hand raised</span>
                    </span>
                  )}
                </div>
              </div>

              {/* Status & Controls */}
              <div className="flex items-center space-x-1 flex-shrink-0">
                {/* Media indicators */}
                <div className="flex items-center space-x-1 text-slate-400 mr-1">
                  {p.audio ? (
                    <Mic className="w-3.5 h-3.5 text-emerald-400" />
                  ) : (
                    <MicOff className="w-3.5 h-3.5 text-rose-400" />
                  )}
                  {p.video ? (
                    <Video className="w-3.5 h-3.5 text-slate-400" />
                  ) : (
                    <VideoOff className="w-3.5 h-3.5 text-rose-400" />
                  )}
                </div>

                {/* Host Actions on other participants */}
                {isHost && !isCurrentUser && (
                  <div className="flex items-center space-x-1 opacity-80 group-hover:opacity-100 transition">
                    <button
                      onClick={() => onMuteParticipant(p.id)}
                      title="Mute participant"
                      className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-rose-400 transition"
                    >
                      <VolumeX className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => onRemoveParticipant(p.id)}
                      title="Remove from meeting"
                      className="p-1.5 rounded-lg hover:bg-rose-500/20 text-slate-400 hover:text-rose-400 transition"
                    >
                      <UserX className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
